import type { PublicClientApplication } from '@azure/msal-browser';
import type { Me } from '@/api/schemas/identity';
import { apiGet, apiPost } from '@/api/client';
import { getRuntimeConfig } from '@/lib/runtimeConfig';

/**
 * The sign-in seam — §6.5.
 *
 * Primary is Entra SSO through MSAL's authorisation-code flow with PKCE
 * (`loginPopup`, not a full-page redirect — it resolves `signInWithSso()` in
 * place, matching the interface below exactly, with no separate
 * redirect-completion step to wire into app bootstrap). Two implementations
 * behind one interface so swapping is a one-line change (`createAuthAdapter`
 * below), and tenant and client id come from `/config.json`, never from a
 * build-time constant.
 *
 * **Tokens live in memory, mirrored into `sessionStorage`.** Spec §6.3's rule
 * was *never `localStorage`*, and the reason behind it is that a token must
 * not outlive the browsing session or leak into another tab. `sessionStorage`
 * satisfies both: it is per-tab and cleared when the tab closes.
 *
 * MSAL keeps its own cache (also `sessionStorage` here, set on the
 * `PublicClientApplication` below) and renews silently through it — but
 * `api/client.ts` and this module's own proactive-refresh timer
 * (`AuthProvider.tsx`) are adapter-agnostic by design, reading one shared
 * `currentAccessToken()`/`accessTokenExpiresAt()` pair rather than knowing
 * MSAL exists. Both adapters write into that same pair on every token they
 * obtain, so the rest of the app never branches on which one is active.
 */
export interface AuthAdapter {
  readonly kind: 'mock' | 'msal';
  /** Resolve the session already established, or `null`. */
  restore(): Promise<Me | null>;
  /** Start the primary sign-in path. */
  signInWithSso(): Promise<Me>;
  /** The local commissioning path. Rejects with an `ApiProblem` when refused. */
  signInWithLocal(username: string, password: string): Promise<Me>;
  /**
   * Exchange the current session for a fresh access token. `false` when the
   * session is genuinely over and the visitor has to sign in again.
   */
  refreshSession(): Promise<boolean>;
  signOut(): Promise<void>;
}

/** In-memory only. Deliberately not exported. */
let accessToken: string | null = null;

/**
 * When the current token stops being accepted, as milliseconds since the
 * epoch, or `null` when the server did not say. Only ever used to refresh
 * *early* — the 401 path is the backstop, and a wrong clock must not be able
 * to sign anybody out.
 */
let expiresAt: number | null = null;

/**
 * Per-tab, and cleared when the tab closes. Every access is wrapped because
 * Safari's private mode throws on `sessionStorage` rather than returning null,
 * and a storage failure must degrade to the old in-memory behaviour rather
 * than break sign-in.
 */
const SESSION_KEY = 'pv.session';

function persistSession(): void {
  try {
    if (accessToken === null) {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ accessToken, expiresAt }));
  } catch {
    // No storage. The session still works until the tab is reloaded.
  }
}

/**
 * Runs once at module load, so the token is in place *before* `AuthProvider`
 * calls `restore()` — otherwise the boot `/me` goes out unauthenticated and
 * the reload signs the visitor out regardless of what is stored.
 */
function rehydrateSession(): void {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw === null) return;

    const stored = JSON.parse(raw) as { accessToken?: unknown; expiresAt?: unknown };
    if (typeof stored.accessToken !== 'string') {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }

    // An expired token is worse than none: it would spend the boot `/me` on a
    // guaranteed 401. Drop it and let the visitor sign in.
    if (typeof stored.expiresAt === 'number' && stored.expiresAt <= Date.now()) {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }

    accessToken = stored.accessToken;
    expiresAt = typeof stored.expiresAt === 'number' ? stored.expiresAt : null;
  } catch {
    // Unreadable or unparseable: treat it as no session at all.
  }
}

rehydrateSession();

export function clearPersistedSession(): void {
  accessToken = null;
  expiresAt = null;
  persistSession();
}

export function currentAccessToken(): string | null {
  return accessToken;
}

export function accessTokenExpiresAt(): number | null {
  return expiresAt;
}

/**
 * `POST /auth/local/login` (T07) — real on the backend regardless of which
 * `AuthAdapter` is active. Local commissioning accounts are a parallel,
 * Entra-independent sign-in path on `pv-app` itself, never something either
 * adapter mocks or gates; both call this same function. Found the hard way:
 * an earlier version had `MsalAuthAdapter.signInWithLocal` unconditionally
 * refuse, on the assumption local sign-in would always be off wherever the
 * real adapter is selected (W-7.4's *eventual* rule, at handover) — but this
 * environment still has `authLocalSignInEnabled: true` during the pilot, so
 * the form renders and a real UDC colleague hit that refusal for real.
 */
async function signInWithLocalAccount(username: string, password: string): Promise<Me> {
  const response = await apiPost<{ accessToken: string; expiresIn?: number; me: Me }>(
    '/auth/local/login',
    { body: { username, password } },
  );
  accessToken = response.accessToken;
  expiresAt = response.expiresIn ? Date.now() + response.expiresIn * 1000 : null;
  persistSession();
  return response.me;
}

/** `POST /auth/local/refresh` (T07) — the local-session half of
 * `refreshSession()`. See {@link signInWithLocalAccount}: shared for the
 * same reason, not specific to either adapter. */
async function refreshLocalSession(): Promise<boolean> {
  try {
    const response = await apiPost<{ accessToken: string; expiresIn?: number }>(
      '/auth/local/refresh',
    );
    accessToken = response.accessToken;
    expiresAt = response.expiresIn ? Date.now() + response.expiresIn * 1000 : null;
    persistSession();
    return true;
  } catch {
    // A refusal here is the session actually being over. Clearing the token
    // is what turns the next call into a clean 401 instead of a retry loop.
    clearPersistedSession();
    return false;
  }
}

export class MockAuthAdapter implements AuthAdapter {
  readonly kind = 'mock' as const;

  async restore(): Promise<Me | null> {
    try {
      return await apiGet<Me>('/me');
    } catch {
      // A rehydrated token the server has since rejected — revoked, or the
      // local account expired — must not survive into the next reload, or
      // every boot spends a request rediscovering the same 401.
      clearPersistedSession();
      return null;
    }
  }

  async signInWithSso(): Promise<Me> {
    // The identity provider is not real, but the session is: signing out ends
    // it in the mock's store, so signing back in has to start one before
    // `/me` will answer. The shape of the flow — establish, then resolve —
    // is what the MSAL adapter will do too.
    const { setSession } = await import('@/mocks/db/store');
    setSession({ signedIn: true });
    return apiGet<Me>('/me');
  }

  signInWithLocal = signInWithLocalAccount;

  refreshSession = refreshLocalSession;

  async signOut(): Promise<void> {
    clearPersistedSession();
    /*
     * The mock's session is server-side state, so clearing a token here would
     * leave `/me` still answering and the next reload silently signed in
     * again. There is no logout endpoint to call — the §5.3 catalogue has
     * none, because a real deployment ends the session at Entra rather than
     * at this API — so the mock adapter ends it in the mock's own store.
     */
    const { setSession } = await import('@/mocks/db/store');
    setSession({ signedIn: false });
  }
}

/**
 * `@azure/msal-browser` (~40 KB gzipped) is loaded here, on demand, rather
 * than imported at the top of this file — it is real product weight only
 * the "entra" branch ever needs, and a static import would put it in every
 * build's first-load chunk regardless of `VITE_USE_MOCKS`, failing the
 * bundle budget (W-7.6, `scripts/check-bundle-size.mjs`) for a mock build
 * that never runs this code at all.
 * <p>
 * A true module-level singleton, on purpose — MSAL's own docs are explicit
 * that a page must construct exactly one `PublicClientApplication` and reuse
 * it everywhere, not one per caller that happens to share a cache. Found the
 * hard way: an earlier version built one here *and* a second, separate one
 * inside `MsalAuthAdapter`, and a real sign-in attempt on the deployed site
 * failed with `interaction_in_progress` — two instances tracking "is an
 * interactive call already running" independently is exactly the class of
 * bug that error exists to report. `completeMsalRedirectIfNeeded` and
 * `MsalAuthAdapter` both call this function, never `new
 * PublicClientApplication(...)` directly.
 */
let msalInstance: Promise<PublicClientApplication> | null = null;

function getMsalInstance(): Promise<PublicClientApplication> {
  msalInstance ??= (async () => {
    const { PublicClientApplication, BrowserCacheLocation } = await import('@azure/msal-browser');
    const { entra } = getRuntimeConfig();
    const pca = new PublicClientApplication({
      auth: {
        clientId: entra.clientId,
        authority: entra.authority,
        // The SPA redirect URI registered in the App Registration is this
        // origin itself (docs/entra-app-registration.md in pv-app) — both
        // the popup flow and its redirect fallback below complete the
        // handshake here, even though a popup never navigates the visible
        // tab away from it.
        redirectUri: window.location.origin,
      },
      cache: {
        // Same rule as this file's own token mirror above: never
        // localStorage, a token must not outlive the tab.
        cacheLocation: BrowserCacheLocation.SessionStorage,
      },
    });
    await pca.initialize();
    return pca;
  })();
  return msalInstance;
}

/**
 * A blocked or closed popup — a browser setting, an extension, or a
 * corporate policy, all realistic on UDC's own machines — not the visitor
 * declining. That is `user_cancelled`, deliberately excluded: falling back
 * to a full-page redirect after someone closes the dialog on purpose would
 * be a worse surprise than the popup simply not reopening.
 */
function isPopupBlockedError(error: unknown): boolean {
  if (!(error instanceof Error) || !('errorCode' in error)) return false;
  const code = (error as { errorCode: unknown }).errorCode;
  return code === 'popup_window_error' || code === 'empty_window_error';
}

/**
 * Completes the popup/redirect "bridge" hop, if this page load is one. Run
 * first, before anything else in `main.tsx` — even before `loadRuntimeConfig`
 * — and if it reports true, `main.tsx` renders nothing at all: this page's
 * only job was relaying an auth response, not being the app.
 * <p>
 * MSAL v5 routes every popup **and** redirect response through this step
 * before the "real" completion (`completeMsalRedirectIfNeeded`,
 * `pca.handleRedirectPromise()`) ever sees it — found live, the hard way: a
 * popup sign-in against the real deployed site got as far as Entra and back,
 * then just… rendered the full login screen again, inside the popup window,
 * instead of closing. `broadcastResponseToMainFrame` (from
 * `@azure/msal-browser/redirect-bridge`, a separate entry point — it needs
 * no `PublicClientApplication`, only the `state` already present in the
 * URL) is what actually posts the popup's result back to the opener and
 * closes the popup, or caches a redirect's result and bounces the tab home
 * to be picked up there. Nothing here was calling it.
 * <p>
 * Throws (by its own documented contract) when the current URL carries no
 * auth response at all — true for every ordinary page view — which is
 * exactly how this tells "a bridge landing" apart from "nothing to do."
 * <p>
 * `broadcastResponseToMainFrame` sets `document.title` unconditionally, as
 * its very first line, before it even checks whether there is a response to
 * process — found live, the second time: every ordinary visit was left with
 * "Microsoft Authentication" as the tab title. Restored on the "nothing to
 * do" path; left alone on a real bridge landing, since that page is either
 * closing or navigating away regardless of its title.
 */
export async function completeMsalBridgeIfNeeded(): Promise<boolean> {
  if (import.meta.env.VITE_USE_MOCKS !== 'false') return false;
  const originalTitle = document.title;
  try {
    const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
    await broadcastResponseToMainFrame();
    return true;
  } catch {
    document.title = originalTitle;
    return false;
  }
}

/**
 * Completes a redirect-based sign-in. Run once at app bootstrap (`main.tsx`),
 * before anything else touches MSAL — including before `AuthProvider` mounts
 * and calls `restore()`.
 * <p>
 * `signInWithSso()`'s primary path is `loginPopup`; this exists only for its
 * fallback. `loginRedirect` sends the whole tab to Entra and back, and MSAL
 * needs this called once on the page that comes back, to consume the auth
 * code in the URL and populate its own cache — after which
 * `MsalAuthAdapter.restore()` finds the account exactly as if the visitor
 * had used the popup path, no different code needed there. This function
 * never touches `currentAccessToken()`/`sessionStorage` directly: the
 * `restore()` call right after it does that, the same way it already does
 * for every other case.
 * <p>
 * Never throws. `main.tsx` awaits this before rendering anything at all —
 * an unhandled rejection here (a genuine Entra-side error carried in the
 * redirect response, e.g. the visitor declined consent) would otherwise
 * leave the whole app stuck on a blank page instead of the login screen,
 * where the same failure is one the visitor can actually see and retry.
 */
export async function completeMsalRedirectIfNeeded(): Promise<void> {
  if (import.meta.env.VITE_USE_MOCKS !== 'false') return; // mirrors createAuthAdapter's own switch
  try {
    const pca = await getMsalInstance();
    await pca.handleRedirectPromise();
  } catch {
    // Nothing to recover here beyond not crashing boot — restore() will
    // find no account and the visitor lands on the login screen, same as
    // any other failed or abandoned sign-in attempt.
  }
}

/**
 * The real adapter. Tenant and client id come from `/config.json`
 * (`getRuntimeConfig().entra`) — Phase 1 runs DunavNET's own dev tenant;
 * Phase 2 (`OPEN-W3`) swaps to UDC's real tenant/client id there, no code
 * change. No Entra group→role map is read anywhere here — role assignment is
 * Parking Management's own, for every account source (`docs/adr/0007-...` in
 * `pv-app`), so this adapter's only job is proving who signed in, never what
 * they may do.
 */
export class MsalAuthAdapter implements AuthAdapter {
  readonly kind = 'msal' as const;

  /** The module-level singleton above — never its own instance. See
   * `getMsalInstance`'s own remarks for why that matters. */
  private instance(): Promise<PublicClientApplication> {
    return getMsalInstance();
  }

  /** The scope requested is this API's own — `api://{client-id}/access_as_user`,
   * the scope `docs/entra-app-registration.md` has the App Registration
   * expose — never Microsoft Graph or anything else. */
  private scope(): string {
    return `api://${getRuntimeConfig().entra.clientId}/access_as_user`;
  }

  private applyResult(result: { accessToken: string; expiresOn: Date | null }): void {
    accessToken = result.accessToken;
    expiresAt = result.expiresOn ? result.expiresOn.getTime() : null;
    persistSession();
  }

  async restore(): Promise<Me | null> {
    // A rehydrated local session already has a token in `sessionStorage`
    // (see `rehydrateSession`, module load) — try it exactly the way
    // `MockAuthAdapter.restore()` does, before reaching into MSAL's cache at
    // all. Local sign-in and Entra sign-in are independent paths on the same
    // real backend; this adapter has to answer for both while
    // `authLocalSignInEnabled` is true (see `signInWithLocalAccount`'s own
    // remarks on why that still matters here).
    if (currentAccessToken()) {
      try {
        return await apiGet<Me>('/me');
      } catch {
        clearPersistedSession();
        return null;
      }
    }

    const pca = await this.instance();
    const [account] = pca.getAllAccounts();
    if (!account) return null;

    try {
      this.applyResult(await pca.acquireTokenSilent({ scopes: [this.scope()], account }));
      return await apiGet<Me>('/me');
    } catch {
      // A silently-expired MSAL session or a token the server has since
      // rejected — either way there is no session to restore, the same
      // outcome MockAuthAdapter.restore() reaches on a rejected token.
      clearPersistedSession();
      return null;
    }
  }

  async signInWithSso(): Promise<Me> {
    const pca = await this.instance();
    try {
      this.applyResult(await pca.loginPopup({ scopes: [this.scope()] }));
    } catch (error) {
      if (!isPopupBlockedError(error)) throw error;
      // Microsoft's own documented pattern for a blocked popup: fall back to
      // a full-page redirect. Fired without awaiting its own resolution —
      // the tab is about to navigate away, and `completeMsalRedirectIfNeeded`
      // (run at the next app boot, in `main.tsx`) is what actually finishes
      // this sign-in, not this call returning. The promise below is left
      // deliberately unresolved so the caller's "Signing in…" state holds
      // until the navigation actually happens, rather than flashing a wrong
      // outcome in the instant before the page unloads.
      void pca.loginRedirect({ scopes: [this.scope()] });
      return new Promise<Me>(() => {});
    }
    return apiGet<Me>('/me');
  }

  // See `signInWithLocalAccount`'s own remarks — real on the backend
  // regardless of which adapter is active, not something to refuse here.
  signInWithLocal = signInWithLocalAccount;

  async refreshSession(): Promise<boolean> {
    // Same split as `restore()`: a local session renews through the local
    // route, not MSAL, and there is no MSAL account to ask for a local
    // session in the first place.
    const pca = await this.instance();
    const [account] = pca.getAllAccounts();
    if (!account) return refreshLocalSession();

    try {
      this.applyResult(await pca.acquireTokenSilent({ scopes: [this.scope()], account }));
      return true;
    } catch {
      // Includes InteractionRequiredAuthError — silent renewal genuinely
      // cannot continue without the visitor present (revoked consent,
      // expired session at Entra). A popup mid-background-refresh would be
      // a surprise, not a fix, so this ends the session instead, the same
      // outcome any other refresh failure here reaches.
      clearPersistedSession();
      return false;
    }
  }

  async signOut(): Promise<void> {
    const pca = await this.instance();
    const [account] = pca.getAllAccounts();
    clearPersistedSession();
    if (account) {
      // Best-effort: ending the session at Entra too, not just locally. A
      // popup the visitor dismisses, or one a blocker stops, must not leave
      // sign-out looking like it failed — the local half above already ran.
      await pca.logoutPopup({ account }).catch(() => {});
    }
  }
}

/**
 * `VITE_USE_MOCKS` is the one switch — the same one `main.tsx` reads to
 * decide whether MSW starts at all. `MockAuthAdapter`'s calls go through the
 * same `apiGet`/`apiPost` as everything else, so it only ever gets a sane
 * answer when MSW is there to intercept them; picking it independently of
 * that switch would silently break the moment the two disagreed.
 */
export function createAuthAdapter(): AuthAdapter {
  return import.meta.env.VITE_USE_MOCKS !== 'false' ? new MockAuthAdapter() : new MsalAuthAdapter();
}
